import { Button, MenuItem, Select } from "@mui/material";
import classNames from "classnames";
import AuthContext from "context/AuthContext";
import dayjs from "dayjs";
import useToastContext from "hooks/useToastContext";
import { isEmpty } from "lodash";
import Pagination from "pages/test_manager/components/Pagination";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TiEye, TiTrash } from "react-icons/ti";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiDelete, apiGet } from "utils/apiFetch";
import { instance } from "utils/apiFetch";
import apiPath from "utils/apiPath";
export default function TestSubmissionsPage() {
  const { t } = useTranslation();
  const notification = useToastContext();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [params] = useSearchParams();
  const testId = params.get("testId");
  const [selectedTestId, setSelectedTestId] = useState(testId || "");
  const [selectedUserStatus, setSelectedUserStatus] = useState("");
  const [selectedPassOutcome, setSelectedPassOutcome] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tests, setTests] = useState();
  const { user } = useContext(AuthContext);
  const permissions = user?.permission?.find(
    (e) => e.manager === "test_manager",
  );
  const fetchTests = useCallback(async () => {
    try {
      const result = await apiGet(apiPath.quizTests, {
        page: 1,
        pageSize: 1000,
      });
      setTests(result?.data?.results?.docs);
    } catch (error) {
      notification.error(error?.response?.data?.message);
    }
  }, [notification]);

  const fetchSubmissions = useCallback(async () => {
    try {
      const result = await apiGet(apiPath.quizTestSubmissions, {
        quizId: selectedTestId,
        userStatus: selectedUserStatus,
        ...(selectedPassOutcome && { passOutcome: selectedPassOutcome }),
        page: page,
        pageSize: 5,
      });
      setSubmissions(result?.data?.results?.docs);
      setTotalPages(result?.data?.results?.totalPages);
    } catch (error) {
      console.log(error);
      notification.error(error?.response?.data?.message);
    }
  }, [
    notification,
    selectedTestId,
    page,
    selectedUserStatus,
    selectedPassOutcome,
  ]);

  useEffect(() => {
    fetchSubmissions();
    fetchTests();
  }, [fetchSubmissions, fetchTests]);

  async function deleteSubmissionHandler(id) {
    try {
      const path = apiPath.quizTestSubmissions + "/" + id;
      const result = await apiDelete(path);
      if (result?.status === 200) {
        notification.success(t("O_SUBMISSION_DELETED_SUCCESSFULLY"));
        fetchSubmissions();
      }
    } catch (error) {
      console.log(error);
      notification.error(t("SOMETHING_WENT_WRONG"));
    }
  }

  async function exportSubmissionsHandler() {
    try {
      const result = await instance.get(apiPath.quizTestSubmissionsExport, {
        params: {
          quizId: selectedTestId,
          ...(selectedUserStatus && { userStatus: selectedUserStatus }),
          ...(selectedPassOutcome && { passOutcome: selectedPassOutcome }),
        },
        responseType: "blob",
      });
      const blob = new Blob([result.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "submissions.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
    }
  }

  return (
    <div className="bg-white px-3 py-4 ">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{t("O_TESTS_SUBMISSIONS")}</h1>
        {permissions?.view && (
          <Button
            onClick={() => exportSubmissionsHandler()}
            variant="contained"
          >
            Export
          </Button>
        )}
      </div>
      <hr className="my-4" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        <div>
          <label>Test</label>
          <Select
            variant="outlined"
            size="small"
            className="w-full"
            onChange={(e) => {
              setSelectedTestId(e.target.value);
              setPage(1);
            }}
            value={selectedTestId}
            label={t("O_TEST")}
          >
            <MenuItem key="All" value="">
              All
            </MenuItem>
            {tests &&
              tests?.map((test) => (
                <MenuItem key={test._id} value={test._id}>
                  {test.title}
                </MenuItem>
              ))}
          </Select>
        </div>
        <div>
          <label>User Status</label>
          <Select
            variant="outlined"
            size="small"
            className="w-full"
            onChange={(e) => {
              setSelectedUserStatus(e.target.value);
              setPage(1);
            }}
            value={selectedUserStatus}
            label={t("O_TEST")}
          >
            <MenuItem key="All" value="">
              All
            </MenuItem>
            <MenuItem key="Active" value="active">
              Active
            </MenuItem>
            <MenuItem key="InActive" value="inactive">
              InActive
            </MenuItem>
          </Select>
        </div>
        <div>
          <label>Grade status</label>
          <Select
            variant="outlined"
            size="small"
            className="w-full"
            onChange={(e) => {
              setSelectedPassOutcome(e.target.value);
              setPage(1);
            }}
            value={selectedPassOutcome}
            label={t("O_TEST")}
          >
            <MenuItem key="pass-all" value="">
              All
            </MenuItem>
            <MenuItem key="pass-passed" value="passed">
              Passed
            </MenuItem>
            <MenuItem key="pass-failed" value="failed">
              Failed
            </MenuItem>
          </Select>
        </div>
      </div>
      <hr className="my-4" />
      <div className="overflow-x-auto">
        <table className="w-full text-xs text-left text-[#A5A5A5] dark:text-gray-400 ">
          <thead className="text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE] dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="py-3 px-6">
                #
              </th>
              <th scope="col" className="py-3 px-6 cursor-pointer">
                {t("O_USER_NAME")}
              </th>

              <th scope="col" className="py-3 px-6 text-center">
                {t("O_TEST_TITLE")}
              </th>
              <th scope="col" className="py-3 px-6">
                {t("O_SCORE")}
              </th>

              <th scope="col" className="py-3 px-6">
                Grade
              </th>
              <th scope="col" className="py-3 px-6">
                Grade Status
              </th>
              <th scope="col" className="py-3 px-6">
                {t("O_STATUS")}
              </th>
              <th scope="col" className="py-3 px-6">
                {t("O_CREATED_AT")}
              </th>

              <th scope="col" className="py-3 px-6 cursor-pointer">
                {t("O_ACTION")}
              </th>
            </tr>
          </thead>
          <tbody>
            {submissions &&
              submissions?.map((item, i) => (
                <tr key={item._id}>
                  <td className="py-3 px-6">{i + 1}</td>
                  <td className="py-3 px-6">
                    {item?.userId?.firstName + " " + item?.userId?.lastName}
                  </td>
                  <td className="py-3 px-6">
                    {item?.quizId?.title ? (
                      <Link
                        to={`/test-manager/tests/details/${item?.quizId?._id}`}
                        className="text-green-400 hover:text-green-700 font-bold"
                      >
                        {item?.quizId?.title}
                      </Link>
                    ) : (
                      "N/A"
                    )}
                  </td>
                  <td className="py-3 px-6 whitespace-nowrap font-bold">
                    {`${item?.totalScore} / ${item?.maxScore}`}
                  </td>
                  <td className="py-3 px-6 whitespace-nowrap font-bold">
                    {((item?.totalScore / item?.maxScore) * 100).toFixed(2)}%
                  </td>
                  <td
                    className={classNames(
                      "py-3 px-6 font-bold",
                      item?.isPassed ? "text-green-500" : "text-red-500",
                    )}
                  >
                    {item?.isPassed ? "Passed" : "Failed"}
                  </td>
                  <td
                    className={classNames(
                      "py-3 px-6 font-bold",
                      item?.status === "graded"
                        ? "text-green-500"
                        : "text-yellow-500",
                    )}
                  >
                    {item?.status === "graded" ? "Graded" : "Pending"}
                  </td>
                  <td className="py-3 px-6">
                    {dayjs(item.createdAt).format("DD-MM-YYYY hh:mm A")}
                  </td>

                  <td className="py-3 px-6">
                    <div className="flex justify-center gap-2">
                      {(permissions?.view || user?.role === "admin") && (
                        <button
                          className="bg-blue-500 text-white p-2 rounded-full"
                          onClick={() =>
                            navigate(
                              `/test-manager/submissions/details/${item._id}`,
                            )
                          }
                        >
                          <TiEye />
                        </button>
                      )}
                      {(permissions?.delete || user?.role === "admin") && (
                        <button
                          className="bg-red-500 text-white p-2 rounded-full"
                          onClick={() => deleteSubmissionHandler(item._id)}
                        >
                          <TiTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            {isEmpty(submissions) && (
              <tr className="bg-white border-b w-full text-center dark:bg-gray-800 dark:border-gray-700">
                <td className="py-4 px-6" colSpan={12}>
                  {t("O_NO_RECORD_FOUND")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <Pagination page={page} setPage={setPage} totalPages={totalPages} />
      </div>
    </div>
  );
}
